/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core';
import cloneDeep from 'lodash.clonedeep';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {
  Devices,
  EntityStatus,
  GenericObject
} from '@ion/core/src/types/entities';
import {getMaxValueInArray, getUniqueArray} from '@ion/core/src/utils/ArrayUtil';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import {Options} from '@ion/dls/src/components/menus/Dropdown';
import {DLSTheme} from '@ion/dls/src/types/types';
import {SelectAllSearch} from '@ion/features/src/search/types';
import {validateServices} from './DeviceServicesUtils';
import ServiceEntries from './ServiceEntries';

export interface ServiceProps extends SelectAllSearch {
  onChange: (service: GenericObject) => void;
  selectedDevices?: Devices;
  serviceOptions: Options[];
  serviceInfo: GenericObject[];
  minService?: number;
  taskName?: string;
  isTaskNameEmpty?: boolean;
  setTaskName?: Function;
};

export interface Entry {
  id: number;
  service: GenericObject;
}

const useStyles = makeStyles((_theme: DLSTheme) => ({
  container : {
    margin : '0px'
  },
  switchLabel : {
    fontSize : '14px'
  }
}));

/**
 * The Service Editor provides the UI which allows a user to update their default
 * service details, such as their service name, status etc..
 *
 *    import {Services} from '@ion/applications/src/pages/devices/Services';
 *
 *    <Services onChange={onChange} />
 *
 */
export const Services = forwardRef(({
  onChange,
  selectedDevices,
  serviceOptions,
  serviceInfo,
  taskName,
  setTaskName,
  totalRowsCount,
  areTotalRowsSelected,
  minService = 0,
  isTaskNameEmpty = false
}: ServiceProps, ref) => {
  const classes                             = useStyles();
  const [serviceEntries, setServiceEntries] = useState<Entry[]>(cloneDeep(serviceInfo) || []);
  const [isDisabled, setIsDisabled]         = useState<boolean>(false);

  const getSelectedServices = useCallback(() => {
    const selectedServiceNames = serviceEntries?.map(({service}) => service.name);
    return getUniqueArray(selectedServiceNames);
  }, [serviceEntries]);

  const getAvailableServices = useCallback(() => {
    const selectedServices = getSelectedServices();
    let availableServices = cloneDeep(serviceOptions);
    if (selectedServices?.length) {
      availableServices = serviceOptions.filter(option => !selectedServices.includes(option.value));
    }
    return availableServices;
  }, [serviceOptions, serviceEntries]);

  const addButtonStatus = useCallback(() => {
    const availableServices = getAvailableServices();
    setIsDisabled(serviceEntries?.length && !availableServices?.length);
  }, [serviceEntries]);

  const onDelete = useCallback(function(e, id) {
    if (serviceEntries?.length > minService) {
      const identifier = Number(id);
      setServiceEntries(serviceEntries.filter((item) => Number(item.id) !== identifier));
    }
  }, [serviceEntries]);

  const addNewEntry = useCallback(function(): void {
    const {isValid, serviceData} = validateServices(serviceEntries);
    if (isValid) {
      const availableServiceOptions = getAvailableServices();
      const allIds = serviceEntries?.map(({id}) => id);
      const maxId = serviceEntries?.length ? getMaxValueInArray(allIds) : 1;
      const newEntry: Entry = {
        id      : maxId + 1,
        service : {
          name    : '',
          status  : EntityStatus.Enabled,
          options : availableServiceOptions || [],
          error   : false
        }
      };
      setServiceEntries([...serviceEntries, newEntry]);
    } else {
      setServiceEntries(serviceData);
    }
  }, [serviceEntries, serviceOptions]);

  const updateEntry = useCallback(function(id: number, name: string, value: string): void {
    const selectedServices = getSelectedServices();
    const serviceExists = selectedServices.includes(value);
    const identifier = Number(id);
    const updatedData = serviceEntries?.map((entry) => {
      if (Number(entry.id) === identifier) {
        if (!serviceExists) {
          entry.service[name] = value;
          entry.service.error = false;
        } else {
          entry.service.error = true;
        }
      }
      return entry;
    });
    setServiceEntries(updatedData);
  }, [serviceEntries]);

  const onUpdateTaskName = useCallback(function({target}) {
    setTaskName(target.value);
  }, []);

  const buildTaskName = useCallback(() => {
    const {length} = getSelectedServices();
    const devicesLen = areTotalRowsSelected ? totalRowsCount : selectedDevices?.length;
    let task = '';
    const deviceStr = devicesLen === 1 ? selectedDevices[0]?.name || selectedDevices[0] : `${devicesLen} devices`;
    if (length > 0) {
      if (length === 1) {
        const {name, status} = serviceEntries[0]?.service;
        task = `${name} ${status} for ${deviceStr}`;
      } else {
        task = `${length} services updated for ${deviceStr}`;
      }
    }
    setTaskName(task);
  }, [selectedDevices, serviceEntries]);

  useEffect(() => {
    onChange?.(serviceEntries);
    addButtonStatus();
  }, [serviceEntries]);

  useEffect(() => {
    buildTaskName();
  }, [selectedDevices, serviceEntries]);

  useEffect(() => {
    if (serviceOptions?.length && minService > 0 && isEmpty(serviceInfo)) {
      addNewEntry();
    }
  }, [serviceOptions, minService, serviceInfo]);

  return (
    <div className={classes.container}>
      <FormSection title={translate('admin-devices:services.manageServices.serviceDetails')}
        legend={<TertiaryButton onClick={addNewEntry} disabled={isDisabled}>
          {translate('contacts:add')}
        </TertiaryButton>}>
        <ServiceEntries serviceEntries={serviceEntries}
          onUpdate={updateEntry}
          onDelete={onDelete} />
      </FormSection>
      <FormSection title={translate('admin-devices:services.manageServices.taskName.title')}>
        <FormItem halfWidth>
          <FormTextField required
            name={'taskName'}
            fieldLabel={translate('admin-devices:services.manageServices.taskName.label')}
            placeholder={translate('admin-devices:services.manageServices.taskName.placeholder')}
            onChange={onUpdateTaskName}
            hasError={isTaskNameEmpty && !taskName}
            errorText={!taskName ? translate('common:editors.shared.required') : ''}
            value={taskName || ''}
            maxLength={64} />
        </FormItem>
      </FormSection>
    </div>
  );
});

export default Services;
